import { createContext, useContext, onMount, onCleanup, createSignal } from "solid-js";
import { createStore } from "solid-js/store";
import socket from "./socket";

// Define the type for connection state
export type ConnectionState = {
  isOffline: boolean;
  socketConnected: boolean;
  connectionAttempts: number;
};

// Create a context for connection management
const ConnectionContext = createContext();

export function ConnectionManagerProvider(props: any) {
  // Create connection store
  const [connectionStore, setConnectionStore] = createStore<ConnectionState>({
    isOffline: !navigator.onLine, // Initial value based on browser's online status
    socketConnected: false,
    connectionAttempts: 0,
  });

  const MAX_CONNECTION_ATTEMPTS = 2; // Switch to offline after 2 failed attempts

  // Socket connection events handlers
  const handleConnect = () => {
    console.log("Socket connected to server");
    setConnectionStore({
      socketConnected: true,
      connectionAttempts: 0,
      isOffline: false,
    });
  };

  const handleConnectError = (error: any) => {
    console.log("Socket connection error:", error);
    
    // Increment connection attempts
    const newAttempts = connectionStore.connectionAttempts + 1;
    setConnectionStore('connectionAttempts', newAttempts);
    
    // If we've reached max attempts, consider server offline
    if (newAttempts >= MAX_CONNECTION_ATTEMPTS) {
      console.log(`Failed to connect ${newAttempts} times, switching to offline mode`);
      setConnectionStore('isOffline', true);
    }
  };

  const handleDisconnect = (reason: any) => {
    console.log("Socket disconnected:", reason);
    setConnectionStore('socketConnected', false);
    
    // If the disconnection wasn't initiated by the client, consider it a server issue
    if (reason === 'io server disconnect' || reason === 'transport close' || reason === 'transport error') {
      const newAttempts = connectionStore.connectionAttempts + 1;
      setConnectionStore('connectionAttempts', newAttempts);
      
      if (newAttempts >= MAX_CONNECTION_ATTEMPTS) {
        console.log(`Disconnected ${newAttempts} times, switching to offline mode`);
        setConnectionStore('isOffline', true);
      }
    }
  };

  // Browser online/offline event handler
  const handleOnlineStatusChange = () => {
    const isOffline = !navigator.onLine;
    console.log(`Device is now ${isOffline ? 'offline' : 'online'}`);
    
    if (isOffline) {
      // Device went offline
      setConnectionStore('isOffline', true);
    } else {
      // Device came online, but we still need to check server availability
      // The server might still be offline even though the device is online
      // We'll let socket connection handle this (don't immediately set to online)
      // Try reconnecting
      socket.connect();
    }
  };

  // Initialize connection manager
  onMount(() => {
    console.log('ConnectionManager mounted');
    
    // Register socket event handlers
    socket.on("connect", handleConnect);
    socket.on("connect_error", handleConnectError);
    socket.on("disconnect", handleDisconnect);
    
    // Register browser online/offline event handlers
    window.addEventListener('online', handleOnlineStatusChange);
    window.addEventListener('offline', handleOnlineStatusChange);
    
    // Initial connection if online
    if (navigator.onLine) {
      console.log('Browser is online, attempting socket connection');
      socket.connect();
    } else {
      console.log('Browser is offline, not attempting socket connection');
      setConnectionStore('isOffline', true);
    }
    
    // Cleanup on unmount
    onCleanup(() => {
      console.log('ConnectionManager unmounted');
      socket.off("connect", handleConnect);
      socket.off("connect_error", handleConnectError);
      socket.off("disconnect", handleDisconnect);
      
      window.removeEventListener('online', handleOnlineStatusChange);
      window.removeEventListener('offline', handleOnlineStatusChange);
    });
  });

  // Expose methods for components to use
  const connectionManager = {
    connect: () => {
      if (navigator.onLine && !socket.connected) {
        socket.connect();
      }
    },
    disconnect: () => {
      socket.disconnect();
    },
    // Method to reset connection attempts counter
    resetConnectionAttempts: () => {
      setConnectionStore('connectionAttempts', 0);
    }
  };

  return (
    <ConnectionContext.Provider value={[connectionStore, setConnectionStore, connectionManager]}>
      {props.children}
    </ConnectionContext.Provider>
  );
}

// Hook for components to access connection context
export function useConnection() {
  return useContext(ConnectionContext);
}